import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useRestaurantBySlug } from '../../lib/hooks';
import { useKitchenStations } from '../../lib/admin-hooks';
import { useMenuCategories, useMenuItems, useMenuItemOptions } from '../../lib/menu-hooks';
import { supabase } from '../../lib/supabase';
import { Skeleton } from '../../components/ui/Skeleton';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Input, Textarea } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { EmptyState } from '../../components/ui/EmptyState';
import { UtensilsCrossed, Plus, Edit2, Trash2, GripVertical, Layers, Settings2, ImageIcon, Upload, X, Search, ListOrdered, FolderTree } from 'lucide-react';
import { MenuReorderDrawer } from '../../components/admin/MenuReorderDrawer';
import { CategoryReorderDrawer } from '../../components/admin/CategoryReorderDrawer';
import { cn, formatCurrency } from '../../lib/utils';
import { MenuCategory, MenuItem, MenuItemOption } from '../../lib/types';
export function MenuManagementPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const {
    restaurant,
    loading: restLoading
  } = useRestaurantBySlug(slug);
  const tenantCtx = (() => {
    try {
      const {
        useTenant
      } = require('../../lib/tenant-context');
      return useTenant();
    } catch {
      return null;
    }
  })();
  const {
    categories,
    loading: catLoading,
    createCategory,
    updateCategory,
    toggleCategory
  } = useMenuCategories(restaurant?.id);
  const {
    items,
    loading: itemsLoading,
    createItem,
    updateItem,
    toggleItem,
    deleteItem
  } = useMenuItems(restaurant?.id);
  const {
    stations
  } = useKitchenStations(restaurant?.id);
  const [activeTab, setActiveTab] = useState<'categories' | 'items'>('items');
  // Modals state
  const [isCatModalOpen, setIsCatModalOpen] = useState(false);
  const [editingCat, setEditingCat] = useState<MenuCategory | null>(null);
  const [isItemModalOpen, setIsItemModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<MenuItem | null>(null);
  const [isOptionsModalOpen, setIsOptionsModalOpen] = useState(false);
  const [managingOptionsForItem, setManagingOptionsForItem] = useState<MenuItem | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategoryId, setFilterCategoryId] = useState<number | ''>('');
  const [isReorderDrawerOpen, setIsReorderDrawerOpen] = useState(false);
  const [isCatReorderDrawerOpen, setIsCatReorderDrawerOpen] = useState(false);
  if (restLoading || !restaurant) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-id="element-3547">
        <Skeleton className="w-16 h-16 rounded-full" data-id="element-3548" />
      </div>;
  }
  const handleOpenCatModal = (cat?: MenuCategory) => {
    setEditingCat(cat || null);
    setIsCatModalOpen(true);
  };
  const handleOpenItemModal = (item?: MenuItem) => {
    setEditingItem(item || null);
    setIsItemModalOpen(true);
  };
  const handleOpenOptionsModal = (item: MenuItem) => {
    setManagingOptionsForItem(item);
    setIsOptionsModalOpen(true);
  };
  return <AdminLayout restaurant={restaurant} role={tenantCtx?.role} onSignOut={tenantCtx?.signOut} data-id="element-3549">
      <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-6" data-id="element-3550">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-3551">
          <div data-id="element-3552">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-3553">
              Gestión de Menú
            </h1>
            <p className="text-gray-500" data-id="element-3554">
              Administra categorías, productos y opciones
            </p>
          </div>
          <div className="flex gap-2" data-id="element-3555">
            <Button variant="secondary" onClick={() => setIsCatReorderDrawerOpen(true)} data-id="element-3556">
              <FolderTree className="w-4 h-4 mr-2" data-id="element-3557" />
              Ordenar Categorías
            </Button>
            <Button variant="secondary" onClick={() => setIsReorderDrawerOpen(true)} data-id="element-3558">
              <ListOrdered className="w-4 h-4 mr-2" data-id="element-3559" />
              Ordenar Productos
            </Button>
            <Button variant={activeTab === 'categories' ? 'primary' : 'secondary'} onClick={() => setActiveTab('categories')} data-id="element-3560">
              <Layers className="w-4 h-4 mr-2" data-id="element-3561" />
              Categorías
            </Button>
            <Button variant={activeTab === 'items' ? 'primary' : 'secondary'} onClick={() => setActiveTab('items')} data-id="element-3562">
              <UtensilsCrossed className="w-4 h-4 mr-2" data-id="element-3563" />
              Productos
            </Button>
          </div>
        </div>

        {activeTab === 'categories' && <div className="space-y-4" data-id="element-3564">
            <div className="flex justify-end" data-id="element-3565">
              <Button onClick={() => handleOpenCatModal()} data-id="element-3566">
                <Plus className="w-4 h-4 mr-2" data-id="element-3567" />
                Nueva Categoría
              </Button>
            </div>

            {catLoading ? <div className="space-y-3" data-id="element-3568">
                {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full rounded-xl" data-id="element-3569" />)}
              </div> : categories.length === 0 ? <EmptyState icon={Layers} title="No hay categorías" description="Crea tu primera categoría para empezar a organizar tu menú." actionLabel="Crear Categoría" onAction={() => handleOpenCatModal()} data-id="element-3570" /> : <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-3571">
                <ul className="divide-y divide-gray-100" data-id="element-3572">
                  {categories.map(cat => <li key={cat.id} className="p-4 flex items-center justify-between hover:bg-gray-50 transition-colors" data-id="element-3573">
                      <div className="flex items-center gap-4" data-id="element-3574">
                        <GripVertical className="w-5 h-5 text-gray-300 cursor-grab" data-id="element-3575" />
                        <div data-id="element-3576">
                          <h3 className="font-bold text-gray-900" data-id="element-3577">
                            {cat.name}
                          </h3>
                          {cat.description && <p className="text-sm text-gray-500" data-id="element-3578">
                              {cat.description}
                            </p>}
                        </div>
                      </div>
                      <div className="flex items-center gap-3" data-id="element-3579">
                        <Badge variant={cat.is_active ? 'success' : 'default'} className="cursor-pointer" onClick={() => toggleCategory(cat.id, !cat.is_active)} data-id="element-3580">
                          {cat.is_active ? 'Activa' : 'Inactiva'}
                        </Badge>
                        <button onClick={() => handleOpenCatModal(cat)} className="p-2 text-gray-400 hover:text-brand-amber transition-colors" data-id="element-3581">
                          <Edit2 className="w-4 h-4" data-id="element-3582" />
                        </button>
                      </div>
                    </li>)}
                </ul>
              </div>}
          </div>}

        {activeTab === 'items' && <div className="space-y-6" data-id="element-3583">
            <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3" data-id="element-3584">
              <div className="relative flex-1 max-w-md" data-id="element-3585">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-3586" />
                <input type="text" placeholder="Buscar producto..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-amber/50 focus:border-brand-amber bg-white" data-id="element-3587" />
                {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" data-id="element-3588">
                    <X className="w-4 h-4" data-id="element-3589" />
                  </button>}
              </div>
              <select value={filterCategoryId} onChange={e => setFilterCategoryId(e.target.value === '' ? '' : Number(e.target.value))} className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-amber/50 focus:border-brand-amber bg-white text-gray-700 min-w-[180px]" data-id="element-3590">
                <option value="" data-id="element-3591">Todas las categorías</option>
                {categories.map(cat => <option key={cat.id} value={cat.id} data-id="element-3592">
                    {cat.name}
                  </option>)}
              </select>
              <Button onClick={() => handleOpenItemModal()} data-id="element-3593">
                <Plus className="w-4 h-4 mr-2" data-id="element-3594" />
                Nuevo Producto
              </Button>
            </div>

            {itemsLoading || catLoading ? <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-3595">
                {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32 w-full rounded-xl" data-id="element-3596" />)}
              </div> : items.length === 0 ? <EmptyState icon={UtensilsCrossed} title="No hay productos" description="Agrega productos a tu menú para que los clientes puedan ordenar." actionLabel="Crear Producto" onAction={() => handleOpenItemModal()} data-id="element-3597" /> : <div className="space-y-8" data-id="element-3598">
                {categories.filter(cat => !filterCategoryId || cat.id === filterCategoryId).map(cat => {
            const catItems = items.filter(i => i.category_id === cat.id && (!searchQuery || i.name.toLowerCase().includes(searchQuery.toLowerCase()) || i.description?.toLowerCase().includes(searchQuery.toLowerCase())));
            if (catItems.length === 0) return null;
            return <div key={cat.id} className="space-y-4" data-id="element-3599">
                        <h2 className="text-lg font-bold text-gray-900 border-b border-gray-200 pb-2" data-id="element-3600">
                          {cat.name}
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-3601">
                          {catItems.map(item => <div key={item.id} className={cn('bg-white rounded-xl border p-4 flex flex-col justify-between transition-all', item.is_active ? 'border-gray-200 shadow-sm' : 'border-gray-200 opacity-60 bg-gray-50')} data-id="element-3602">
                              <div className="flex justify-between items-start mb-2" data-id="element-3603">
                                <div data-id="element-3604">
                                  <h3 className="font-bold text-gray-900 text-lg" data-id="element-3605">
                                    {item.name}
                                  </h3>
                                  <p className="text-brand-amber font-bold" data-id="element-3606">
                                    {formatCurrency(item.price)}
                                  </p>
                                </div>
                                <div className="flex items-center gap-2" data-id="element-3607">
                                  {item.station_code ? <Badge variant="default" className="text-[10px] uppercase" data-id="element-3608">
                                      {item.station_code}
                                    </Badge> : <Badge variant="warning" className="text-[10px]" data-id="element-3609">
                                      ⚠️ Sin estación
                                    </Badge>}
                                  <Badge variant={item.is_active ? 'success' : 'default'} className={cn('cursor-pointer', !item.station_code && item.is_active && 'opacity-50')} onClick={() => {
                        if (!item.station_code && !item.is_active) {
                          alert('Debes asignar una estación de preparación antes de activar este producto.');
                          return;
                        }
                        toggleItem(item.id, !item.is_active);
                      }} data-id="element-3610">
                                    {item.is_active ? 'Activo' : 'Inactivo'}
                                  </Badge>
                                </div>
                              </div>
                              {item.description && <p className="text-sm text-gray-500 mb-4 line-clamp-2" data-id="element-3611">
                                  {item.description}
                                </p>}
                              <div className="flex justify-end gap-2 mt-auto pt-4 border-t border-gray-100" data-id="element-3612">
                                <Button variant="secondary" size="sm" onClick={() => handleOpenOptionsModal(item)} data-id="element-3613">
                                  <Settings2 className="w-4 h-4 mr-1.5" data-id="element-3614" />
                                  Opciones
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleOpenItemModal(item)} data-id="element-3615">
                                  <Edit2 className="w-4 h-4 mr-1.5" data-id="element-3616" />
                                  Editar
                                </Button>
                              </div>
                            </div>)}
                        </div>
                      </div>;
          })}
              </div>}
          </div>}
      </div>

      {/* Modals */}
      <CategoryModal isOpen={isCatModalOpen} onClose={() => setIsCatModalOpen(false)} category={editingCat} onSave={async data => {
      let result;
      if (editingCat) {
        result = await updateCategory(editingCat.id, data);
      } else {
        result = await createCategory(data);
      }
      if (result?.success) {
        setIsCatModalOpen(false);
      }
      return result;
    }} data-id="element-3617" />

      <ItemModal isOpen={isItemModalOpen} onClose={() => setIsItemModalOpen(false)} item={editingItem} categories={categories} stations={stations} onSave={async data => {
      let result;
      if (editingItem) {
        result = await updateItem(editingItem.id, data);
      } else {
        result = await createItem(data as any);
      }
      if (result?.success) {
        setIsItemModalOpen(false);
      }
      return result;
    }} onDelete={async () => {
      if (editingItem) {
        if (confirm('¿Estás seguro de eliminar este producto?')) {
          const result = await deleteItem(editingItem.id);
          if (result?.success) {
            setIsItemModalOpen(false);
          }
          return result;
        }
      }
      return {
        success: false
      };
    }} data-id="element-3618" />

      {managingOptionsForItem && <OptionsModal isOpen={isOptionsModalOpen} onClose={() => setIsOptionsModalOpen(false)} item={managingOptionsForItem} restaurantId={restaurant.id} data-id="element-3619" />}

      <MenuReorderDrawer restaurantId={restaurant.id} isOpen={isReorderDrawerOpen} onClose={() => setIsReorderDrawerOpen(false)} data-id="element-3620" />

      <CategoryReorderDrawer restaurantId={restaurant.id} slug={slug} isOpen={isCatReorderDrawerOpen} onClose={() => setIsCatReorderDrawerOpen(false)} data-id="element-3621" />
    </AdminLayout>;
}
// ─── Modals ──────────────────────────────────────────────────
function CategoryModal({
  isOpen,
  onClose,
  category,
  onSave
}: {
  isOpen: boolean;
  onClose: () => void;
  category: MenuCategory | null;
  onSave: (data: any) => Promise<any>;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (isOpen) {
      setName(category?.name || '');
      setDescription(category?.description || '');
      setIsActive(category?.is_active ?? true);
      setError(null);
    }
  }, [isOpen, category]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    console.log('[NOIZA][UI] CategoryModal handleSubmit called with:', {
      name,
      description,
      is_active: isActive
    });
    try {
      const result = await onSave({
        name,
        description,
        is_active: isActive
      });
      console.log('[NOIZA][UI] CategoryModal onSave result:', result);
      if (result && !result.success) {
        setError(result.error || 'Error al guardar la categoría');
      }
    } catch (err: any) {
      console.error('[NOIZA][UI] CategoryModal onSave EXCEPTION:', err);
      setError(err?.message || 'Error inesperado al guardar');
    }
    setIsSubmitting(false);
  };
  return <Modal isOpen={isOpen} onClose={onClose} title={category ? 'Editar Categoría' : 'Nueva Categoría'} data-id="element-3622">
      <form onSubmit={handleSubmit} className="space-y-4" data-id="element-3623">
        <Input label="Nombre" value={name} onChange={e => setName(e.target.value)} required placeholder="Ej: Entradas, Bebidas, Postres" data-id="element-3624" />
        <Textarea label="Descripción (opcional)" value={description} onChange={e => setDescription(e.target.value)} placeholder="Breve descripción de la categoría" data-id="element-3625" />
        <div className="flex items-center gap-2 pt-2" data-id="element-3626">
          <input type="checkbox" id="cat-active" checked={isActive} onChange={e => setIsActive(e.target.checked)} className="w-4 h-4 text-brand-amber rounded border-gray-300 focus:ring-brand-amber" data-id="element-3627" />
          <label htmlFor="cat-active" className="text-sm font-medium text-gray-700" data-id="element-3628">
            Categoría activa (visible para clientes)
          </label>
        </div>
        {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3" data-id="element-3629">
            {error}
          </div>}
        <div className="pt-4 flex justify-end gap-3" data-id="element-3630">
          <Button type="button" variant="ghost" onClick={onClose} data-id="element-3631">
            Cancelar
          </Button>
          <Button type="submit" isLoading={isSubmitting} data-id="element-3632">
            Guardar
          </Button>
        </div>
      </form>
    </Modal>;
}
function ItemModal({
  isOpen,
  onClose,
  item,
  categories,
  stations,
  onSave,
  onDelete
}: {
  isOpen: boolean;
  onClose: () => void;
  item: MenuItem | null;
  categories: MenuCategory[];
  stations: any[];
  onSave: (data: any) => Promise<any>;
  onDelete: () => Promise<any>;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [stationCode, setStationCode] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [existingImageUrl, setExistingImageUrl] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const imageInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (isOpen) {
      setName(item?.name || '');
      setDescription(item?.description || '');
      setPrice(item?.price?.toString() || '');
      setCategoryId(item?.category_id || (categories[0]?.id ?? ''));
      setStationCode(item?.station_code || '');
      setIsActive(item?.is_active ?? true);
      setError(null);
      setImageFile(null);
      setImagePreview(null);
      setExistingImageUrl(item?.image_url || null);
      setUploadingImage(false);
    }
  }, [isOpen, item, categories]);
  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setError('Solo se permiten imágenes JPG, PNG o WebP');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('La imagen no puede superar 5MB');
      return;
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setExistingImageUrl(null);
    setError(null);
  };
  const removeImage = () => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImageFile(null);
    setImagePreview(null);
    setExistingImageUrl(null);
    if (imageInputRef.current) imageInputRef.current.value = '';
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    // Validate station_code is required if product is active AND stations exist
    if (isActive && !stationCode && stations.length > 0) {
      setError('Debes asignar una estación de preparación para activar este producto.');
      return;
    }
    setIsSubmitting(true);
    // Upload image if a new file was selected
    let imageUrl: string | null = existingImageUrl;
    if (imageFile) {
      setUploadingImage(true);
      try {
        const ext = imageFile.name.split('.').pop() || 'jpg';
        const safeName = name.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '') || 'product';
        const path = `restaurant-${item?.restaurant_id || 'new'}/${Date.now()}-${safeName}.${ext}`;
        const {
          error: uploadError
        } = await supabase.storage.from('menu-images').upload(path, imageFile, {
          upsert: true,
          contentType: imageFile.type
        });
        if (uploadError) {
          console.error('[MenuMgmt] Image upload error:', uploadError);
          setError(`Error al subir imagen: ${uploadError.message}`);
          setIsSubmitting(false);
          setUploadingImage(false);
          return;
        }
        const {
          data: urlData
        } = supabase.storage.from('menu-images').getPublicUrl(path);
        imageUrl = urlData.publicUrl;
      } catch (err: any) {
        setError(`Error al subir imagen: ${err?.message || 'Error desconocido'}`);
        setIsSubmitting(false);
        setUploadingImage(false);
        return;
      }
      setUploadingImage(false);
    }
    const payload = {
      name,
      description,
      price: parseFloat(price),
      category_id: categoryId,
      station_code: stationCode || null,
      is_active: isActive,
      image_url: imageUrl
    };
    console.log('[NOIZA][UI] ItemModal handleSubmit called with:', payload);
    try {
      const result = await onSave(payload);
      console.log('[NOIZA][UI] ItemModal onSave result:', result);
      if (result && !result.success) {
        setError(result.error || 'Error al guardar el producto');
      }
    } catch (err: any) {
      console.error('[NOIZA][UI] ItemModal onSave EXCEPTION:', err);
      setError(err?.message || 'Error inesperado al guardar');
    }
    setIsSubmitting(false);
  };
  return <Modal isOpen={isOpen} onClose={onClose} title={item ? 'Editar Producto' : 'Nuevo Producto'} data-id="element-3633">
      <form onSubmit={handleSubmit} className="space-y-4" data-id="element-3634">
        <Input label="Nombre del producto" value={name} onChange={e => setName(e.target.value)} required data-id="element-3635" />

        {/* Image Upload */}
        <div data-id="element-3636">
          <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-3637">
            Foto del producto
          </label>
          {imagePreview || existingImageUrl ? <div className="relative w-full h-40 rounded-xl overflow-hidden border border-gray-200 bg-gray-50" data-id="element-3638">
              <img src={imagePreview || existingImageUrl || ''} alt="Preview" className="w-full h-full object-cover" data-id="element-3639" />
              <button type="button" onClick={removeImage} className="absolute top-2 right-2 w-7 h-7 bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center transition-colors" data-id="element-3640">
                <X className="w-4 h-4" data-id="element-3641" />
              </button>
              <button type="button" onClick={() => imageInputRef.current?.click()} className="absolute bottom-2 right-2 px-3 py-1.5 bg-black/60 hover:bg-black/80 text-white text-xs font-medium rounded-lg transition-colors" data-id="element-3642">
                Cambiar
              </button>
            </div> : <button type="button" onClick={() => imageInputRef.current?.click()} className="w-full h-32 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center gap-2 text-gray-400 hover:border-gray-400 hover:text-gray-500 hover:bg-gray-50 transition-all cursor-pointer" data-id="element-3643">
              <Upload className="w-6 h-6" data-id="element-3644" />
              <span className="text-sm font-medium" data-id="element-3645">Subir foto</span>
              <span className="text-xs" data-id="element-3646">PNG, JPG o WebP (máx 5MB)</span>
            </button>}
          <input ref={imageInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleImageSelect} className="hidden" data-id="element-3647" />
        </div>

        <div className="grid grid-cols-2 gap-4" data-id="element-3648">
          <Input label="Precio" type="number" min="0" step="0.01" value={price} onChange={e => setPrice(e.target.value)} required data-id="element-3649" />
          <div data-id="element-3650">
            <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-3651">
              Categoría
            </label>
            <select value={categoryId} onChange={e => setCategoryId(e.target.value)} required className="w-full h-11 px-3 py-2 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-amber" data-id="element-3652">
              <option value="" disabled data-id="element-3653">
                Seleccionar...
              </option>
              {categories.map(c => <option key={c.id} value={c.id} data-id="element-3654">
                  {c.name}
                </option>)}
            </select>
          </div>
        </div>

        <div data-id="element-3655">
          <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-3656">
            Estación de preparación (KDS){' '}
            {isActive && stations.length > 0 && <span className="text-red-500" data-id="element-3657">*</span>}
          </label>
          {stations.length > 0 ? <select value={stationCode} onChange={e => setStationCode(e.target.value)} className={cn('w-full h-11 px-3 py-2 rounded-xl border bg-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-amber', isActive && !stationCode && stations.length > 0 ? 'border-red-300 bg-red-50' : 'border-gray-200')} data-id="element-3658">
              <option value="" data-id="element-3659">Seleccionar estación...</option>
              {stations.map(s => <option key={s.id} value={s.code} data-id="element-3660">
                  {s.name}
                </option>)}
            </select> : <div className="w-full h-11 px-3 py-2 rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-400 flex items-center" data-id="element-3661">
              No hay estaciones configuradas — se omitirá
            </div>}
          <p className={cn('text-xs mt-1', isActive && !stationCode && stations.length > 0 ? 'text-red-600 font-medium' : 'text-gray-500')} data-id="element-3662">
            {stations.length === 0 ? 'No se requiere estación. Puedes configurar estaciones desde el panel de administración.' : isActive && !stationCode ? '⚠️ Requerido para productos activos. Determina en qué pantalla de cocina aparecerá.' : 'Determina en qué pantalla de cocina aparecerá este producto.'}
          </p>
        </div>

        <Textarea label="Descripción" value={description} onChange={e => setDescription(e.target.value)} data-id="element-3663" />

        <div className="flex items-center gap-2 pt-2" data-id="element-3664">
          <input type="checkbox" id="item-active" checked={isActive} onChange={e => {
          const newActive = e.target.checked;
          if (newActive && !stationCode && stations.length > 0) {
            setError('Debes asignar una estación de preparación antes de activar este producto.');
            return;
          }
          setIsActive(newActive);
          setError(null);
        }} className="w-4 h-4 text-brand-amber rounded border-gray-300 focus:ring-brand-amber" data-id="element-3665" />
          <label htmlFor="item-active" className="text-sm font-medium text-gray-700" data-id="element-3666">
            Producto activo (visible para clientes)
          </label>
        </div>

        {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3" data-id="element-3667">
            {error}
          </div>}
        <div className="pt-4 flex justify-between items-center" data-id="element-3668">
          {item ? <Button type="button" variant="danger" onClick={onDelete} data-id="element-3669">
              Eliminar
            </Button> : <div data-id="element-3670" />}
          <div className="flex gap-3" data-id="element-3671">
            <Button type="button" variant="ghost" onClick={onClose} data-id="element-3672">
              Cancelar
            </Button>
            <Button type="submit" isLoading={isSubmitting} data-id="element-3673">
              Guardar
            </Button>
          </div>
        </div>
      </form>
    </Modal>;
}
function OptionsModal({
  isOpen,
  onClose,
  item,
  restaurantId
}: {
  isOpen: boolean;
  onClose: () => void;
  item: MenuItem;
  restaurantId: string;
}) {
  const {
    options,
    loading,
    createOption,
    deleteOption
  } = useMenuItemOptions(item.id);
  const [name, setName] = useState('');
  const [priceDelta, setPriceDelta] = useState('');
  const [group, setGroup] = useState('Opciones');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    await createOption({
      restaurant_id: restaurantId,
      option_group: group,
      name,
      price_delta: parseFloat(priceDelta) || 0
    });
    setName('');
    setPriceDelta('');
    setIsSubmitting(false);
  };
  return <Modal isOpen={isOpen} onClose={onClose} title={`Opciones: ${item.name}`} data-id="element-3674">
      <div className="space-y-6" data-id="element-3675">
        {/* List existing options */}
        {loading ? <Skeleton className="h-20 w-full rounded-xl" data-id="element-3676" /> : options.length > 0 ? <div className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden" data-id="element-3677">
            <ul className="divide-y divide-gray-200" data-id="element-3678">
              {options.map(opt => <li key={opt.id} className="p-3 flex justify-between items-center" data-id="element-3679">
                  <div data-id="element-3680">
                    <p className="font-medium text-sm text-gray-900" data-id="element-3681">
                      {opt.name}
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-3682">
                      Grupo: {opt.option_group}
                    </p>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-3683">
                    <span className="text-sm font-bold text-gray-900" data-id="element-3684">
                      {opt.price_delta > 0 ? '+' : ''}
                      {formatCurrency(opt.price_delta)}
                    </span>
                    <button onClick={() => deleteOption(opt.id)} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors" data-id="element-3685">
                      <Trash2 className="w-4 h-4" data-id="element-3686" />
                    </button>
                  </div>
                </li>)}
            </ul>
          </div> : <p className="text-sm text-gray-500 text-center py-4" data-id="element-3687">
            Este producto no tiene opciones adicionales.
          </p>}

        {/* Add new option form */}
        <form onSubmit={handleAdd} className="bg-white p-4 rounded-xl border border-gray-200 space-y-3" data-id="element-3688">
          <h4 className="font-bold text-sm text-gray-900" data-id="element-3689">Agregar Opción</h4>
          <div className="grid grid-cols-2 gap-3" data-id="element-3690">
            <Input placeholder="Nombre (ej: Extra Queso)" value={name} onChange={e => setName(e.target.value)} required data-id="element-3691" />
            <Input type="number" step="0.01" placeholder="Precio extra (ej: 500)" value={priceDelta} onChange={e => setPriceDelta(e.target.value)} required data-id="element-3692" />
          </div>
          <Input placeholder="Grupo (ej: Extras, Tamaños)" value={group} onChange={e => setGroup(e.target.value)} required data-id="element-3693" />
          <Button type="submit" fullWidth isLoading={isSubmitting} size="sm" data-id="element-3694">
            Agregar Opción
          </Button>
        </form>
      </div>
    </Modal>;
}